import React, { useState, useEffect, useRef } from "react";
import { Reorder } from "framer-motion";
import { X } from "lucide-react";

export const StreamingPlatformModal = ({ isOpen, onClose, allPlatforms, onSave }) => {
    const [platforms, setPlatforms] = useState([]);
    const platformRefs = useRef({});
    const inputRefs = useRef({});
    const [highlightedId, setHighlightedId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setPlatforms(JSON.parse(JSON.stringify(allPlatforms)));
        }
    }, [isOpen, allPlatforms]);

    if (!isOpen) return null;

    const updatePlatformUrl = (id, newUrl) => {
        setPlatforms(prev =>
            prev.map(p => (p.id === id ? { ...p, url: newUrl } : p))
        );
    };

    const deletePlatform = (id) => {
        setPlatforms(prev =>
            prev.flatMap(p => {
                if (p.id !== id) return p;

                if (p.type === "custom") return [];
                return { ...p, url: "" };
            })
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                    <div>
                        <h2 className="text-lg sm:text-2xl font-bold text-theme_clr1">
                            Streaming Platforms
                        </h2>
                        <p className="text-gray-500 text-xs sm:text-sm mt-1">
                            Edit your streaming links
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-6 overflow-y-auto flex-1">
                    <Reorder.Group
                        axis="y"
                        values={platforms}
                        onReorder={setPlatforms}
                        className="space-y-3"
                    >
                        {platforms.map(platform => (
                            <Reorder.Item
                                key={platform.id}
                                value={platform}
                                ref={(el) => (platformRefs.current[platform.id] = el)}
                            >
                                <div
                                    className={`
                                        ${platform.bgColor}
                                        rounded-xl p-3 sm:p-4
                                        flex flex-col sm:flex-row
                                        gap-3 sm:gap-4
                                        transition-all duration-300
                                        ${highlightedId === platform.id ? "ring-4 ring-theme_clr2 ring-opacity-60" : ""}
                                    `}
                                >

                                    {/* Top Row */}
                                    <div className="flex items-center justify-between sm:justify-start gap-3">

                                        {/* Left */}
                                        <div className="flex items-center gap-3">

                                            {/* Drag */}
                                            <div className="cursor-grab active:cursor-grabbing">
                                                <svg width="20" height="20" viewBox="0 0 24 24">
                                                    <circle cx="9" cy="6" r="1.5" />
                                                    <circle cx="15" cy="6" r="1.5" />
                                                    <circle cx="9" cy="12" r="1.5" />
                                                    <circle cx="15" cy="12" r="1.5" />
                                                    <circle cx="9" cy="18" r="1.5" />
                                                    <circle cx="15" cy="18" r="1.5" />
                                                </svg>
                                            </div>

                                            {/* Icon + Name */}
                                            <div className="flex items-center gap-2 min-w-[130px]">
                                                <img
                                                    src={platform.icon}
                                                    className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                                                />
                                                <span className="text-sm sm:text-base font-medium text-theme_clr1">
                                                    {platform.name}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Mobile delete */}
                                        <div className="sm:hidden">
                                            <button onClick={() => deletePlatform(platform.id)}>
                                                <i className="bi bi-x-circle text-xl text-theme_clr2"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Input + Desktop Delete */}
                                    <div className="flex items-center gap-3 w-full">

                                        <input
                                            type="text"
                                            value={platform.url}
                                            ref={(el) => (inputRefs.current[platform.id] = el)}
                                            onChange={(e) => updatePlatformUrl(platform.id, e.target.value)}
                                            className="
                                                w-full sm:flex-1
                                                bg-white rounded-xl px-4 py-2 sm:py-3
                                                text-sm sm:text-base
                                                text-theme_clr1 font-medium
                                                outline-none focus:border-2 focus:border-theme_clr1
                                            "
                                            placeholder={`Enter ${platform.name} link`}
                                        />

                                        {/* Desktop delete */}
                                        <div className="hidden sm:flex items-center">
                                            <button onClick={() => deletePlatform(platform.id)}>
                                                <i className="bi bi-x-circle text-2xl text-theme_clr2"></i>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </Reorder.Item>
                        ))}
                    </Reorder.Group>
                </div>

                {/* Footer */}
                <div className="p-3 sm:p-6 border-t flex flex-col sm:flex-row gap-3 sm:justify-end">

                    <button
                        onClick={() => {
                            onSave(platforms);
                            onClose();
                        }}
                        className="w-full sm:w-auto px-8 py-2 sm:py-3 bg-theme_clr1 text-white font-semibold rounded-full"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};